import React, { useCallback, useState } from 'react';
import FinalForm from '@/Framework/UI/Organisms/FinalForm';
import validate from './validator';
import ChangePermissionsSection from '../ChangePermissionsSection';
import FolderIcon from '@/dataroom/ui/common/FilesystemIcon/FolderIcon';
import FormSection from '../FormSection';
import { useFilesystemContext } from '@/dataroom/application/filesystem/filesystemActions/FilesystemContext';
import Input from '@dealroadshow/uikit/core/components/Input';
import { IChangePermissionsType } from '@/dataroom/domain/vo/filesystem/ChangePermissionsType';
import { IFilesystemListItem } from '@/dataroom/domain/vo/collection/FilesystemListItem';
import { IFolderTree } from '@/dataroom/domain/vo/filesystem/FolderTree';
import { PermissionGroup } from '@/dataroom/domain/vo/types/PermissionGroup';
import { TFinalFormRenderProp } from '@/Framework/UI/Organisms/FinalForm/interfaces';
import styles from './editFolderForm.scss';

interface IProps {
  initialValues: {},
  isFetching: boolean,
  item: IFilesystemListItem | IFolderTree,
  closeModal: () => void,
  onSubmit: (
    formData: {
      folderId: number,
      folderName: string,
    },
    permissionGroups: Partial<{
      [key in PermissionGroup]?: number[];
    }>,
    shouldUpdate: boolean,
  ) => Promise<{ code: number } | null>,
  userHasSystemManageAccess: boolean,
  isPermissionsSectionVisible: boolean,
  isUserAdminLite: boolean,
  onShowPermissionsSection: () => void,
  name: string,
  footerRefCallback: () => void,
}

const EDIT_FOLDER_TIP = (
  <>
    <b>Tip:</b> Note that if a folder is assigned to a permission, any sub-files and sub-folders subsequently added to
    the data room will match its permission. Permissions can be set at the folder or file level in Manage
    Permissions
    Groups, within Settings.
  </>
);

const EditFolderForm = (
  {
    initialValues,
    isFetching,
    item,
    closeModal,
    onSubmit,
    userHasSystemManageAccess,
    isPermissionsSectionVisible,
    isUserAdminLite,
    onShowPermissionsSection,
    name,
    footerRefCallback,
  }: IProps,
) => {
  const { allEditPermissions: { updatePermissions } } = useFilesystemContext();

  const [permissions, setPermissions] = useState<IChangePermissionsType>({});
  const {
    allItems,
    selectedItems,
    disabledIds,
    defaultCheckboxGroupName,
    ...permissionGroups
  } = permissions;
  let shouldUpdate = false;
  const handleFormSubmit = (formData) => onSubmit(formData, permissionGroups, shouldUpdate)
    .then(closeModal)
    .then(updatePermissions);

  const renderForm: TFinalFormRenderProp = useCallback(({
    invalid,
    pristine,
    dirtyFields,
    modifiedSinceLastSubmit,
    hasSubmitErrors,
    hasValidationErrors,
  }, {
    Field,
  }) => {
    shouldUpdate = !!Object.keys(dirtyFields).length;
    return (
      <>
        <FormSection
          pristine={ pristine }
          hasSubmitErrors={ hasSubmitErrors }
          hasValidationErrors={ hasValidationErrors }
          modifiedSinceLastSubmit={ modifiedSinceLastSubmit }
          isFetching={ isFetching }
          closeModal={ closeModal }
          isPermissionsSectionVisible={ isPermissionsSectionVisible }
          isUserAdminLite={ isUserAdminLite }
          userHasSystemManageAccess={ userHasSystemManageAccess }
          onShowPermissionsSection={ onShowPermissionsSection }
          name={ name }
          footerRefCallback={ footerRefCallback }
        >
          <FolderIcon
            className={ styles.folderIcon }
            isStaging={ item.isStaging }
          />
          <div className={ styles.titleWrp }>
            Rename the folder "<b>{ item.name }</b>" to:
          </div>
          <div className={ styles.inputWrp }>
            <Field
              name="folderName"
              placeholder="Folder name"
              component={ Input }
              dataTest="folderNameInput"
              autoFocus
            />
          </div>
        </FormSection>
        { userHasSystemManageAccess && isPermissionsSectionVisible && (
          <ChangePermissionsSection
            invalid={ invalid }
            pristine={ pristine }
            closeModal={ closeModal }
            parentFolderId={ item.id }
            onChange={ setPermissions }
            tip={ EDIT_FOLDER_TIP }
            isFetching={ isFetching }
            isCheckboxVisible
            permissionGroups={ permissions }
            isFolder
            name={ name }
            footerRefCallback={ footerRefCallback }
          />
        ) }
      </>
    );
  }, [
    isFetching,
    isPermissionsSectionVisible,
    userHasSystemManageAccess,
    permissionGroups,
  ]);

  return (
    <FinalForm
      name="editFolderForm"
      dataTest="editFolderForm"
      initialValues={ initialValues }
      onSubmit={ handleFormSubmit }
      render={ renderForm }
      validate={ validate }
    />
  );
};

export default EditFolderForm;
